/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack, StackItem } from '@fluentui/react/lib/Stack';
import { mergeStyles } from '@fluentui/react/lib/Styling';
import { getId } from '@fluentui/react/lib/Utilities';
import React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme, getStylist } from 'src/core/themes/luisStylist';
import { PerformanceMetricsBreakdown } from 'src/modules/modelDetails/stores/ModelEvaluationStore';

export type ModelConfusionMatrixProps = {
    modelConfusionMatrix: number[][];
    performanceMetricsBreakdown: PerformanceMetricsBreakdown[];
};

const messages = defineMessages({
    labeledAs: {
        id: 'modelConfusionMatrix.labeledAs',
        defaultMessage: 'Labeled as'
    },
    predictedAs: {
        id: 'modelConfusionMatrix.predictedAs',
        defaultMessage: 'Predicted as'
    }
});

const { getClassName } = getStylist('ModelConfusionMatrix');
const theme = getScopedTheme('ModelConfusionMatrix');

const basicTextContainerClassName = getClassName('basicTextContainerClassName', { height: '48px', display: 'flex', alignItems: 'center' });
const basicTextClassName = getClassName('basicTextClassName', {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
});
const labelClassName = mergeStyles(basicTextContainerClassName, { width: '104px', justifyContent: 'center', border: '1px solid white' });
const labelTextClassName = mergeStyles(basicTextClassName, { minWidth: '96px', textAlign: 'center' });
const predictionClassName = mergeStyles(basicTextContainerClassName, {
    borderBottom: theme.predictionBorderBottom,
    width: '112px',
    justifyItems: 'left'
});
const predictionTextClassName = mergeStyles(basicTextClassName, { minWidth: '112px' });
const basicCellClassName = getClassName('basicCellClassName', {
    height: '48px',
    fontSize: '14px',
    minWidth: '104px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    border: '1px solid white'
});
const mainCellClassName = mergeStyles(basicCellClassName, { backgroundColor: theme.mainCellBackgroundColor });
const tpCellClassName = mergeStyles(basicCellClassName, { color: 'white', backgroundColor: theme.tpCellBackgroundColor });

export const ModelConfusionMatrix = (props: ModelConfusionMatrixProps) => {
    const { modelConfusionMatrix, performanceMetricsBreakdown } = props;
    const { localize } = useLocalization();

    const getCelTitle = (value: number, labeledName: string, predictedName: string) =>
        `${value}\n${localize(messages.labeledAs)}: ${labeledName}, ${localize(messages.predictedAs)}: ${predictedName}`;

    return (
        <Stack styles={{ root: { overflow: 'auto', maxHeight: '80vh' } }}>
            <StackItem tokens={{ padding: '0 0 0 140px' }}>
                <LuisText fontWeight={600}>{localize(messages.labeledAs)}</LuisText>
            </StackItem>

            <Stack horizontal verticalAlign="end" tokens={{ childrenGap: 60 }}>
                <LuisText fontWeight={600} style={{ minWidth: '80px' }}>
                    {localize(messages.predictedAs)}
                </LuisText>

                <Stack horizontal styles={{ root: { marginLeft: '12px' } }}>
                    {performanceMetricsBreakdown.map((performanceMetric, index) => (
                        <div key={`${performanceMetric.name}-${index}`} className={labelClassName}>
                            <LuisTooltip styles={{ root: labelTextClassName }} content={performanceMetric.name}>
                                <LuisText className={labelTextClassName}>{performanceMetric.name}</LuisText>
                            </LuisTooltip>
                        </div>
                    ))}
                </Stack>
            </Stack>

            <Stack tokens={{ padding: '0 25px' }} tabIndex={0}>
                {performanceMetricsBreakdown.map((performanceMetric, index) => (
                    <Stack key={`${performanceMetric.name}-${index}`} horizontal verticalAlign="center">
                        <div className={predictionClassName}>
                            <LuisTooltip styles={{ root: predictionTextClassName }} content={performanceMetric.name}>
                                <LuisText className={predictionTextClassName}>{performanceMetric.name}</LuisText>
                            </LuisTooltip>
                        </div>

                        {modelConfusionMatrix[index].map((val, idx) => (
                            <LuisText
                                key={getId()}
                                color={idx === index && theme.tpCellTextColor}
                                className={idx === index ? tpCellClassName : mainCellClassName}
                                title={getCelTitle(val, performanceMetricsBreakdown[idx].name, performanceMetric.name)}>
                                {val}
                            </LuisText>
                        ))}
                    </Stack>
                ))}
            </Stack>
        </Stack>
    );
};
